import { Button } from "@/components/ui/button";
import { watches } from "@/definitions/diaryTypes";
import type { Watch } from "@/definitions/diaryTypes";
import type { StatefulDiaryT } from "@/types/database/diaries";
import {
	randomHappyEmoji,
	wordsOfBoost,
	wordsOfEncouragement,
	wordsOfPraise,
} from "@/utils/positivePatientWords";
import { CheckCircle2, ExternalLink, Smile, X } from "lucide-react";
import { useState } from "react";

interface DiaryProcessInfoProps {
	diary: StatefulDiaryT;
}

const DiaryProcessInfo = ({ diary }: DiaryProcessInfoProps) => {
	const [isVisible, setIsVisible] = useState(true);
	const watchType = diary.diary_type?.toUpperCase() as Watch;
	const watch = watches[watchType];

	if (!isVisible) return null;

	const renderContent = () => {
		switch (diary.diary_type) {
			case "HEELPAIN":
				if (diary.tracker_due) {
					const isFirstEntry = diary.trackers_view?.every(
						(tracker) => !tracker.entries_view?.length,
					);

					return (
						<>
							<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
								{isFirstEntry
									? `Welcome to Your Heel Pain Diary! ${randomHappyEmoji()}`
									: "Time to Update Your Heel Pain Diary"}
							</h3>

							{isFirstEntry ? (
								<>
									<p className="mb-2 text-slate-700 animate-fade-in">
										Let's start by recording your past treatments and current
										pain levels.
									</p>
									<p className="text-slate-700 text-sm">
										<strong>What's next:</strong> After this, you'll have
										check-ins at 3 and 6 months, with weekly pain scores in
										between.
									</p>
								</>
							) : (
								<>
									<p className="mb-2 text-slate-700 animate-fade-in">
										Your regular updates help us track your progress and ensure
										you're following the plan.
									</p>
									<p className="mb-2 text-slate-700 animate-fade-in">
										{wordsOfEncouragement()}
									</p>
								</>
							)}
						</>
					);
				}

				if (diary.next_entry) {
					return (
						<>
							<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
								Heel Pain Progress
							</h3>
							<p className="mb-2 text-slate-700 animate-fade-in">
								Your regular updates help us track your recovery journey.
							</p>
							<div className="flex items-center gap-2 mt-3 text-slate-700 animate-fade-in">
								<Smile size={20} className="text-primary" />
								<span className="font-medium">{wordsOfBoost()}</span>
							</div>
						</>
					);
				}

				return (
					<>
						<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
							Heel Pain Diary Complete
						</h3>
						<div className="flex items-center gap-2 mt-3 text-slate-700 animate-fade-in">
							<CheckCircle2 size={20} className="text-primary" />
							<span className="font-medium">{wordsOfPraise()}</span>
						</div>
						<p className="mt-2 text-slate-700 animate-fade-in">
							You've completed all required entries. No further action needed.
						</p>
					</>
				);

			case "ARTHROSAMID":
			case "ARTHROSAMID_FORTIUS":
			case "ARTHROSAMID_CITIUS":
				if (diary.tracker_due) {
					const isFirstEntry = diary.trackers_view?.every(
						(tracker) => !tracker.entries_view?.length,
					);

					return (
						<>
							<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
								{isFirstEntry
									? `Welcome to Your Arthrosamid Diary! ${randomHappyEmoji()}`
									: "Time to Update Your Knee Pain Diary"}
							</h3>

							{isFirstEntry ? (
								<>
									<p className="mb-2 text-slate-700 animate-fade-in">
										Let's start by recording your baseline pain and function
										levels.
									</p>
									<p className="text-slate-700 text-sm">
										<strong>Good news:</strong> Future entries will be much
										shorter. At 3 months, you'll only need to complete 2 quick
										forms.
									</p>
								</>
							) : (
								<>
									<p className="mb-2 text-slate-700 animate-fade-in">
										Your updates help us track how well the treatment is
										working.
									</p>
									<p className="mb-2 text-slate-700 animate-fade-in">
										{wordsOfEncouragement()}
									</p>
								</>
							)}
						</>
					);
				}

				if (diary.next_entry) {
					return (
						<>
							<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
								Arthrosamid Progress
							</h3>
							<p className="mb-2 text-slate-700 animate-fade-in">
								Your regular updates help us track your recovery journey.
							</p>
							<div className="flex items-center gap-2 mt-3 text-slate-700 animate-fade-in">
								<Smile size={20} className="text-primary" />
								<span className="font-medium">{wordsOfBoost()}</span>
							</div>
						</>
					);
				}

				return (
					<>
						<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
							Arthrosamid Diary Complete
						</h3>
						<div className="flex items-center gap-2 mt-3 text-slate-700 animate-fade-in">
							<CheckCircle2 size={20} className="text-primary" />
							<span className="font-medium">{wordsOfPraise()}</span>
						</div>
						<p className="mt-2 text-slate-700 animate-fade-in">
							You've completed all required entries. No further action needed.
						</p>
					</>
				);

			default:
				if (diary.tracker_due) {
					return (
						<>
							<h3 className="mb-2 font-semibold text-slate-950 text-lg animate-fade-in">
								Time to Update Your Pain Diary
							</h3>
							<p className="mb-2 text-slate-700 animate-fade-in">
								Please update your pain levels. This helps your healthcare team
								make better decisions about your care.
							</p>
							<p className="mb-2 text-slate-700 animate-fade-in">
								{wordsOfEncouragement()}
							</p>
						</>
					);
				}

				if (diary.next_entry) {
					return (
						<>
							<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
								Pain Diary Progress
							</h3>
							<p className="mb-2 text-slate-700 animate-fade-in">
								Your regular updates are making a real difference.
							</p>
							<div className="flex items-center gap-2 mt-3 text-slate-700 animate-fade-in">
								<Smile size={20} className="text-primary" />
								<span className="font-medium">{wordsOfEncouragement()}</span>
							</div>
							<p className="mt-2 text-slate-700 animate-fade-in">
								Tracking your pain helps identify patterns and improve your
								care.
							</p>
						</>
					);
				}

				return (
					<>
						<h3 className="mb-2 font-medium text-slate-800 text-lg animate-fade-in">
							Pain Diary Complete
						</h3>
						<div className="flex items-center gap-2 mt-3 text-slate-700 animate-fade-in">
							<CheckCircle2 size={20} className="text-primary" />
							<span className="font-medium">{wordsOfPraise()}</span>
						</div>
						<p className="mt-2 text-slate-700 animate-fade-in">
							You've completed all required entries. No further action needed.
						</p>
					</>
				);
		}
	};

	return (
		<div className="relative bg-gradient-to-br from-slate-50/40 via-slate-50/80 to-indigo-50/50 shadow-sm hover:shadow-md my-4 border border-indigo-100 rounded-lg w-full overflow-hidden text-sm transition-all duration-300">
			<div className="top-0 left-0 absolute bg-primary/20 w-full h-1" />
			<button
				type="button"
				onClick={() => setIsVisible(false)}
				className="top-2 right-2 absolute hover:bg-slate-100 p-1 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
				aria-label="Close"
			>
				<X size={18} />
			</button>

			<div className="p-4">
				{renderContent()}

				<div className="flex justify-between items-center mt-6 pt-4 border-slate-100 border-t">
					{watch?.infoLink && (
						<a
							href={watch.infoLink}
							target={watch.infoLink.startsWith("http") ? "_blank" : undefined}
							rel={
								watch.infoLink.startsWith("http")
									? "noopener noreferrer"
									: undefined
							}
							className="flex items-center gap-1 text-primary hover:text-primary/80 text-sm underline underline-offset-2 transition-colors"
							title={`Learn more about ${watch.name}`}
						>
							Learn More
							{watch.infoLink.startsWith("http") && (
								<ExternalLink size={14} className="inline" />
							)}
						</a>
					)}
					<Button
						variant="outline"
						size="sm"
						onClick={() => setIsVisible(false)}
						className="hover:bg-primary/5 ml-auto transition-colors"
					>
						Got it
					</Button>
				</div>
			</div>
		</div>
	);
};

export default DiaryProcessInfo;
